/**
 * Pure consolidation calculations.
 *
 * Re-implements the math from `mock.js` (the existing accelerate-calculator)
 * but as a stateless function that takes the proposal payload and returns a
 * `calculated` snapshot. Used by:
 *   • Broker editor (live-recalc as values change)
 *   • Slide deck (renders headline numbers)
 *   • Public revisit page (recomputes from saved payload — no PDF)
 */

const monthlyAmortPayment = (principal, annualRatePct, years) => {
  if (!principal || !years) return 0;
  const r = (Number(annualRatePct) || 0) / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const debtMonthly = (debt) => monthlyAmortPayment(debt.amount, debt.rate, debt.term);

const debtTotalInterest = (debt) => {
  const m = debtMonthly(debt);
  const months = (Number(debt.term) || 0) * 12;
  return m * months - (Number(debt.amount) || 0);
};

/** Run the consolidation calc. payload shape:
 *    { debts:[{name,amount,rate,term}], mortgage:{remainingBalance,interestRate,remainingTerm}, consolidationRate }
 * Returns: { current, consolidated, redrawStrategy, minRepayments }
 */
export const computeConsolidation = (payload) => {
  const debts = Array.isArray(payload?.debts) ? payload.debts : [];
  const mortgage = payload?.mortgage || { remainingBalance: 0, interestRate: 0, remainingTerm: 0 };
  const consolidationRate = Number(payload?.consolidationRate) || 0;

  const totalDebtAmount = debts.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const totalDebtMonthly = debts.reduce((s, d) => s + debtMonthly(d), 0);
  const totalDebtInterest = debts.reduce((s, d) => s + debtTotalInterest(d), 0);
  const weightedRate = totalDebtAmount
    ? debts.reduce((s, d) => s + (Number(d.amount) || 0) * (Number(d.rate) || 0), 0) / totalDebtAmount
    : 0;

  const currentMortgageMonthly = monthlyAmortPayment(
    mortgage.remainingBalance,
    mortgage.interestRate,
    mortgage.remainingTerm,
  );
  const totalMonthlyPayment = totalDebtMonthly + currentMortgageMonthly;

  const newMortgageAmount = (Number(mortgage.remainingBalance) || 0) + totalDebtAmount;
  const consolidatedMonthly = monthlyAmortPayment(
    newMortgageAmount,
    consolidationRate,
    mortgage.remainingTerm,
  );
  const consolidatedTotalInterest = consolidatedMonthly * (mortgage.remainingTerm || 0) * 12 - newMortgageAmount;

  const monthlySavings = Math.max(0, totalMonthlyPayment - consolidatedMonthly);

  // Redraw strategy: keep paying current outlay, extra goes to principal.
  // Approximate via amortisation with monthly rate, extra payment per month.
  const r = (consolidationRate || 0) / 100 / 12;
  const extra = monthlySavings;
  let balance = newMortgageAmount;
  let monthsRedraw = 0;
  let interestPaid = 0;
  const monthlyTotalSpend = consolidatedMonthly + extra;
  const cap = (mortgage.remainingTerm || 30) * 12;
  while (balance > 0 && monthsRedraw < cap * 2) {
    const i = balance * r;
    interestPaid += i;
    let principalPaid = monthlyTotalSpend - i;
    if (principalPaid > balance) principalPaid = balance;
    balance -= principalPaid;
    monthsRedraw += 1;
    if (monthlyTotalSpend <= i) break; // safety: can't amortise
  }

  const totalRemainingMonths = (mortgage.remainingTerm || 0) * 12;
  const monthsSaved = Math.max(0, totalRemainingMonths - monthsRedraw);
  const yearsSaved = +(monthsSaved / 12).toFixed(1);
  const totalInterestSavings = (totalDebtInterest + (currentMortgageMonthly * totalRemainingMonths - (mortgage.remainingBalance || 0))) - interestPaid;

  // Minimum repayment over a 30-yr standard term — used in the headline tile
  const minMonthly = monthlyAmortPayment(newMortgageAmount, consolidationRate, 30);
  const minRepayments = {
    monthly: minMonthly,
    fortnightly: (minMonthly * 12) / 26,
    weekly: (minMonthly * 12) / 52,
  };

  return {
    current: {
      totalDebtAmount,
      totalDebtMonthly,
      totalDebtInterest,
      averageRate: +weightedRate.toFixed(2),
      monthlyMortgagePayment: currentMortgageMonthly,
      totalMonthlyPayment,
      totalInterest: totalDebtInterest + (currentMortgageMonthly * totalRemainingMonths - (mortgage.remainingBalance || 0)),
    },
    consolidated: {
      newMortgageAmount,
      monthlyPayment: consolidatedMonthly,
      monthlySavings,
      totalInterest: consolidatedTotalInterest,
      // Savings from consolidation ALONE (no redraw strategy applied):
      // current total interest minus consolidated total interest.
      totalInterestSavings: Math.max(
        0,
        (totalDebtInterest + (currentMortgageMonthly * totalRemainingMonths - (mortgage.remainingBalance || 0))) - consolidatedTotalInterest,
      ),
    },
    redrawStrategy: {
      monthlyPayment: monthlyTotalSpend,
      monthsSaved,
      yearsSaved,
      totalInterest: interestPaid,
      totalSavings: Math.max(0, totalInterestSavings),
      // Extra savings from applying redraw on top of consolidation
      // (consolidated total interest minus redraw-strategy total interest).
      additionalSavings: Math.max(0, consolidatedTotalInterest - interestPaid),
    },
    minRepayments,
  };
};

export const formatCurrency = (n) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
